import { v4 } from "uuid";

import type {
  CuratedPlaceOfSafety,
  IncidentWorkItemStatus,
} from "@thalamos/common";
import { IncidentWorkItemStatusSchema } from "@thalamos/common";
import {
  FormSectionSchema,
  IncidentFormIdSchema,
  IncidentFormTypeSchema,
  type ExtendedVisionUser,
  type FormSection,
  type IncidentFormId,
  type IncidentWorkItemId,
} from "@thalamos/vision-common";

import api from "../api/index.js";
import {
  createTriagedIncident,
  pickRandomPlaceOfSafety,
  quickCreateContextOf,
} from "./quick_create.js";
import {
  getQuickCreateMcaSampleData,
  type PersonName,
} from "./quickCreateSampleData.js";

/**
 * Creates an MCA incident populated up to and including arrival at the
 * Emergency Department, so it is ready to share with NHS, without completing
 * every form page by hand.
 *
 * Anything the caller does not supply is fabricated: omit `name` or
 * `curatedPlaceOfSafety` and a random one is used.
 *
 * A testing aid: callers must gate on `isQuickCreateEnabled`. Throws on the
 * first failed step, leaving a part-built incident behind, so the caller can
 * report what went wrong.
 */
export async function runQuickCreateMca({
  user,
  name,
  curatedPlaceOfSafety,
}: {
  user: ExtendedVisionUser;
  name?: PersonName;
  curatedPlaceOfSafety?: CuratedPlaceOfSafety;
}): Promise<IncidentWorkItemId> {
  // TODO(mca-enablement): the server should refuse an MCA triage for an
  // organisation without the MCA form; until it does, check here.
  if (!user.sessionOrganisationConfiguration?.mentalCapacityActEnabled) {
    throw new Error("The current organisation does not use the MCA form");
  }

  const context = quickCreateContextOf(user);
  const { sessionContext } = context;

  const sampleData = getQuickCreateMcaSampleData(context.workflowVersion, {
    name,
    curatedPlaceOfSafety:
      curatedPlaceOfSafety ??
      (await pickRandomPlaceOfSafety({ posType: "emergencyDepartment" })),
  });

  const { incidentWorkItemId, formId: mcaFormId } = await createTriagedIncident(
    {
      context,
      sampleData,
      formType: IncidentFormTypeSchema.enum.mentalCapacityAct,
    },
  );

  // A section that fails validation leaves Share unavailable without saying
  // why, so every write is checked.
  const updateSection = async ({
    formId,
    section,
    data,
    status,
  }: {
    formId: IncidentFormId;
    section: FormSection;
    data: Record<string, unknown>;
    status?: IncidentWorkItemStatus;
  }) => {
    const response = await api.incidentWorkItem.updateData(
      incidentWorkItemId,
      formId,
      section,
      data,
      true,
      status,
    );
    if (response.status !== 204) {
      throw new Error(
        `Failed to update ${section}: ${String(response.status)}`,
      );
    }
  };

  await updateSection({
    formId: mcaFormId,
    section: FormSectionSchema.enum.mcaPersonDetails,
    data: sampleData.mcaPersonDetails,
  });
  await updateSection({
    formId: mcaFormId,
    section: FormSectionSchema.enum.mcaIncidentDetails,
    // The sample's command unit is a placeholder uuid, so the session
    // organisation replaces it.
    data: {
      ...sampleData.mcaIncidentDetails,
      bcuIncidentTookPlace: {
        value: sessionContext.organisationId,
        valueLabel: sessionContext.organisationName,
      },
    },
  });
  await updateSection({
    formId: mcaFormId,
    section: FormSectionSchema.enum.mcaCapacityAssessment,
    data: sampleData.mcaCapacityAssessment,
  });
  await updateSection({
    formId: mcaFormId,
    section: FormSectionSchema.enum.mcaAccompaniment,
    data: sampleData.mcaAccompaniment,
  });

  const placeOfSafetyFormId = IncidentFormIdSchema.parse(v4());
  const placeOfSafetyResponse =
    await api.incidentWorkItem.createPlaceOfSafetyForm(
      incidentWorkItemId,
      placeOfSafetyFormId,
    );
  if (placeOfSafetyResponse.status !== 204) {
    throw new Error(
      `Failed to create place of safety form: ${String(placeOfSafetyResponse.status)}`,
    );
  }

  await updateSection({
    formId: placeOfSafetyFormId,
    section: FormSectionSchema.enum.recordPlaceOfSafetyMain,
    data: sampleData.recordPlaceOfSafetyMain,
  });
  await updateSection({
    formId: placeOfSafetyFormId,
    section: FormSectionSchema.enum.recordPlaceOfSafetyArrival,
    data: sampleData.recordPlaceOfSafetyArrival,
    status: IncidentWorkItemStatusSchema.enum["arrived-at-place-of-safety"],
  });

  return incidentWorkItemId;
}
